/* Minori · account & exchange.
 *
 * Quello che il backend NON dà, e che quindi qui non compare inventato:
 *  - le chiavi API: `@Crud` le esclude dalle risposte. Non ci sono nemmeno in
 *    forma mascherata, quindi la riga dice che non sono leggibili invece di
 *    mostrare otto pallini che fingerebbero di essere una chiave;
 *  - i permessi della chiave (sola lettura / trading / prelievo): nessun
 *    endpoint li espone. La richiesta è in RICHIESTE-API.md;
 *  - il saldo "libero" separato da quello impegnato: `get-all-balances`
 *    restituisce un totale, polveri comprese. Si chiama "saldo totale", non
 *    "saldo libero": è la stessa parola del prototipo su un numero diverso.
 *
 * I soli momenti in cui un segreto attraversa questa interfaccia sono la
 * creazione di un account e la sostituzione delle sue chiavi: viaggiano nel
 * corpo della richiesta, non vengono registrate, non finiscono in localStorage
 * né in una query string, e i campi si svuotano appena la richiesta è partita.
 *
 * Nella modifica i campi delle chiavi partono VUOTI e vuoti significano
 * "lascia quelle che ci sono": non si possono rileggere, quindi precompilarli
 * è impossibile, e mandarli vuoti li cancellerebbe.
 */

import { useEffect, useMemo, useState } from 'react';
import { Avvertenza, Distintivo, Pulsante, Scheda, Selettore } from '../../componenti';
import { AzioneDistruttiva } from '../../componenti/AzioneDistruttiva';
import { Campo } from '../../componenti/Modulo';
import { Modale, usaAvvisi, Avvisi } from '../../componenti/Sovrapposizioni';
import { ContenitoreDati, Scheletro, ScheletroLista } from '../../componenti/Stati';
import { accounts, exchanges } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { usaRichiesta } from '../../api/usaRichiesta';
import { daQuanto, num, valuta } from '../../lib/formato';
import type { Account, Exchange, SaldoAccount } from '../../api/tipi';
import { SezioneExchange } from './Exchange';
import './minori.css';

export function AccountExchange() {
  const { avvisi, mostra } = usaAvvisi();
  const [apreAggiunta, setApreAggiunta] = useState(false);
  const [daModificare, setDaModificare] = useState<Account | null>(null);
  const [daRimuovere, setDaRimuovere] = useState<Account | null>(null);
  const [inRimozione, setInRimozione] = useState(false);

  const elenco = usaRichiesta(() => accounts.elenco({ sort: ['id,ASC'] }), []);
  const listaExchange = usaRichiesta(() => exchanges.elenco({ sort: ['name,ASC'] }), []);

  /* I saldi sono la chiamata lenta: N conti interrogati uno per volta, senza
     timeout complessivo. Sta in una richiesta a parte perché l'elenco dei
     conti deve comparire subito, con i numeri che arrivano dopo. */
  const saldi = usaRichiesta((segnale) => accounts.saldiTutti(segnale), []);

  const perAccount = useMemo(() => {
    const m = new Map<number, SaldoAccount>();
    for (const b of saldi.dati?.balances ?? []) {
      if (b.account?.id != null) m.set(b.account.id, b);
    }
    return m;
  }, [saldi.dati]);

  const lista = elenco.dati?.data ?? [];
  const listaExchangeDati: Exchange[] = listaExchange.dati?.data ?? [];

  /* Quanti account per exchange: serve alla sezione exchange per dire cosa si
     rompe cambiando uno slug. Si calcola dalla lista già scaricata, senza una
     richiesta in più. */
  const accountPerExchange = useMemo(() => {
    const m = new Map<number, number>();
    for (const a of lista) m.set(a.exchangeId, (m.get(a.exchangeId) ?? 0) + 1);
    return m;
  }, [lista]);

  async function rimuovi() {
    if (!daRimuovere) return;
    setInRimozione(true);
    try {
      await accounts.elimina(daRimuovere.id);
      mostra(`Account "${daRimuovere.name}" rimosso.`, 'riuscito');
      setDaRimuovere(null);
      elenco.ricarica();
    } catch (e) {
      mostra(e instanceof ErroreApi ? e.message : 'Rimozione non riuscita.', 'fallito');
    } finally {
      setInRimozione(false);
    }
  }

  return (
    <div className="sb-colonna">
      <Avvisi avvisi={avvisi} />

      <div className="sb-riga">
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 'var(--sb-peso-semi)', fontSize: 'var(--sb-t-14)' }}>
            Account collegati
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
            {saldi.stato === 'caricamento'
              ? 'lettura dei saldi in corso: un exchange per volta'
              : saldi.letturaA
                ? /* Il totale si scrive solo se è stato letto: un conto a saldo
                     zero e un conto il cui saldo non è arrivato sono due cose
                     diverse, e con `?? 0` sullo schermo erano identiche —
                     entrambe «$0,00». */
                  `saldi letti ${daQuanto(saldi.letturaA)} · ${
                    saldi.dati?.total == null
                      ? 'totale non disponibile'
                      : `totale ${valuta(saldi.dati.total, '$', 2)}`
                  }`
                : 'saldi non letti'}
          </div>
        </div>
        <Pulsante variante="primario" onClick={() => setApreAggiunta(true)}>
          Aggiungi
        </Pulsante>
      </div>

      <ContenitoreDati
        stato={elenco.stato}
        causaVuoto="Nessun account collegato: senza un account nessun bot può operare su un exchange."
        azioneVuoto={{ etichetta: 'Aggiungi il primo', onClick: () => setApreAggiunta(true) }}
        messaggioErrore={elenco.errore?.message}
        onRiprova={elenco.ricarica}
        scheletro={<ScheletroLista righe={3} />}
      >
        <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
          {lista.map((a) => (
            <SchedaAccount
              key={a.id}
              account={a}
              saldo={perAccount.get(a.id) ?? null}
              saldiInCorso={saldi.stato === 'caricamento'}
              onModifica={() => setDaModificare(a)}
              onRimuovi={() => setDaRimuovere(a)}
            />
          ))}
        </div>
      </ContenitoreDati>

      {saldi.stato === 'errore' && (
        <Avvertenza livello="cautela">
          I saldi non sono arrivati: {saldi.errore?.message}. La lettura interroga ogni exchange in
          sequenza e non ha un timeout complessivo, quindi con molti conti può scadere prima di
          finire. Gli account restano leggibili senza i numeri.
        </Avvertenza>
      )}

      <Avvertenza livello="nota">
        Le chiavi API non tornano mai dal backend: `@Crud` le esclude dalle risposte. Qui non c'è
        niente da mascherare perché non c'è niente da mostrare.
      </Avvertenza>

      <SezioneExchange
        lista={listaExchangeDati}
        stato={listaExchange.stato}
        messaggioErrore={listaExchange.errore?.message}
        onRicarica={listaExchange.ricarica}
        contaAccount={accountPerExchange}
        onAvviso={mostra}
      />

      <ModuloAccount
        aperto={apreAggiunta}
        account={null}
        onChiudi={() => setApreAggiunta(false)}
        exchangeDisponibili={listaExchangeDati}
        onSalvato={(nome) => {
          mostra(`Account "${nome}" creato.`, 'riuscito');
          setApreAggiunta(false);
          elenco.ricarica();
        }}
      />

      <ModuloAccount
        aperto={daModificare != null}
        account={daModificare}
        onChiudi={() => setDaModificare(null)}
        exchangeDisponibili={listaExchangeDati}
        onSalvato={(nome) => {
          mostra(`Account "${nome}" aggiornato.`, 'riuscito');
          setDaModificare(null);
          elenco.ricarica();
          /* Le chiavi possono essere cambiate: i saldi vecchi sono stati letti
             con quelle di prima, quindi si rileggono invece di restare a
             schermo come se nulla fosse. */
          saldi.ricarica();
        }}
      />

      {/* La conferma ripete il conto e quanto c'è dentro: senza il numero
          davanti, "rimuovi" è una parola come un'altra. */}
      <Modale
        aperta={daRimuovere != null}
        onChiudi={() => setDaRimuovere(null)}
        titolo="Rimuovi l'account"
        sottotitolo={daRimuovere?.name}
        pieda={
          <AzioneDistruttiva
            etichetta="Rimuovi l'account"
            etichettaArmata="Tocca di nuovo per rimuovere"
            nota="I fondi restano sull'exchange; SemBot perde le chiavi per operarci."
            onConferma={rimuovi}
            disabilitata={inRimozione}
          />
        }
      >
        {daRimuovere && (
          <div className="sb-colonna">
            <Scheda variante="incassata">
              <div className="sb-riga">
                <span className="sb-etichetta" style={{ flex: 1 }}>
                  Exchange
                </span>
                <span className="sb-m-voce__valore">{daRimuovere.exchange?.name ?? `#${daRimuovere.exchangeId}`}</span>
              </div>
              <hr className="sb-divisore" />
              <div className="sb-riga">
                <span className="sb-etichetta" style={{ flex: 1 }}>
                  Saldo letto
                </span>
                <span className="sb-m-voce__valore">
                  {perAccount.get(daRimuovere.id)?.total != null
                    ? valuta(perAccount.get(daRimuovere.id)!.total, '$', 2)
                    : 'n/d'}
                </span>
              </div>
            </Scheda>
            <Avvertenza livello="grave">
              Se un tradebot sta operando su questo account, resta senza chiavi e le sue operazioni
              falliranno. Ferma prima i bot che lo usano.
            </Avvertenza>
          </div>
        )}
      </Modale>
    </div>
  );
}

/* ==========================================================================
 * Scheda di un conto
 * ======================================================================== */

function SchedaAccount({
  account,
  saldo,
  saldiInCorso,
  onModifica,
  onRimuovi,
}: {
  account: Account;
  saldo: SaldoAccount | null;
  saldiInCorso: boolean;
  onModifica: () => void;
  onRimuovi: () => void;
}) {
  /* `isCCXTSupported` è calcolato a runtime: `false` significa che lo slug non
     esiste più nella versione di ccxt installata. È il caso che faceva cadere
     le pagine del frontend vecchio, quindi qui si mostra, non si ignora. */
  const nonSupportato = account.exchange && account.exchange.isCCXTSupported === false;
  const inErrore = Boolean(saldo?.error);

  return (
    <article className={`sb-m-account${nonSupportato || inErrore ? ' sb-m-account--attenzione' : ''}`}>
      <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontWeight: 'var(--sb-peso-forte)', fontSize: 'var(--sb-t-135)' }}>
            {account.name}
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
            {account.exchange?.name ?? `exchange #${account.exchangeId}`}
            {account.exchange?.slug ? ` · ${account.exchange.slug}` : ''}
            {account.description ? ` · ${account.description}` : ''}
          </div>
        </div>
        <Distintivo tono={nonSupportato ? 'cautela' : 'neutro'}>
          {nonSupportato ? 'non più in ccxt' : account.exchange?.isCCXT ? 'via ccxt' : 'nativo'}
        </Distintivo>
      </div>

      <div className="sb-m-riquadri">
        <div className="sb-m-riquadro">
          <div className="sb-etichetta">Saldo totale</div>
          <div className="sb-m-riquadro__valore">
            {saldiInCorso ? (
              <Scheletro larghezza="70%" altezza={13} />
            ) : saldo ? (
              valuta(saldo.total, '$', 2)
            ) : (
              'n/d'
            )}
          </div>
        </div>
        <div className="sb-m-riquadro">
          <div className="sb-etichetta">Polveri sotto 1 $</div>
          <div className="sb-m-riquadro__valore">
            {saldiInCorso ? (
              <Scheletro larghezza="55%" altezza={13} />
            ) : saldo ? (
              `${num(saldo.dust.count)} monete · ${valuta(saldo.dust.value, '$', 2)}`
            ) : (
              'n/d'
            )}
          </div>
        </div>
      </div>

      {saldo?.error && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          {/* Il messaggio arriva dal server già in italiano. */}
          <Avvertenza livello="cautela">{saldo.error}</Avvertenza>
        </div>
      )}

      {nonSupportato && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="cautela">
            La libreria ccxt non riconosce più lo slug "{account.exchange?.slug}". I bot su questo
            account non possono operare finché lo slug non viene corretto.
          </Avvertenza>
        </div>
      )}

      <div className="sb-riga" style={{ marginTop: 'var(--sb-s-6)', gap: 'var(--sb-s-4)' }}>
        <span className="sb-nota" style={{ flex: 1, minWidth: 180 }}>
          chiavi non leggibili dal browser: il backend non le restituisce, in nessuna forma
        </span>
        <Pulsante variante="tenue" onClick={onModifica}>
          Modifica…
        </Pulsante>
        {/* La rimozione sta per ultima, dopo la modifica: si tocca per sbaglio
            quella che sta prima, non quella che sta dopo. */}
        <Pulsante variante="tenue" onClick={onRimuovi}>
          Rimuovi…
        </Pulsante>
      </div>
    </article>
  );
}

/* ==========================================================================
 * Il modulo, uno solo per creazione e modifica
 *
 * È l'unico punto in cui un segreto attraversa il browser, e le due modalità
 * si comportano in modo diverso proprio lì:
 *
 *  - in CREAZIONE chiave e segreto sono obbligatori. Lo impone il backend:
 *    `@IsNotEmpty({ groups: [CREATE] })` su `apiKey`, `apiSecret` ed
 *    `exchangeId`. Un account senza chiavi non è un account a metà, è un
 *    account che non può leggere nemmeno un saldo;
 *  - in MODIFICA i campi partono vuoti e vuoti vogliono dire "lascia quelle
 *    che ci sono". Non c'è alternativa: `@Crud` esclude `apiKey`, `apiSecret` e
 *    `apiPassphrase` dalle risposte, quindi non si possono precompilare — e
 *    mandarli vuoti li sovrascriverebbe con niente, lasciando l'account cieco.
 *    Per questo la PATCH li include SOLO se sono stati riscritti.
 *
 * L'exchange, in modifica, è di sola lettura. Non è una dimenticanza: le chiavi
 * salvate valgono per l'exchange su cui sono state emesse. Spostare l'account
 * altrove terrebbe le chiavi vecchie e le presenterebbe al posto sbagliato. Chi
 * deve cambiare exchange sostituisce anche le chiavi, e allora tanto vale un
 * account nuovo — che si può fare senza toccare quello che sta operando.
 * ======================================================================== */

function ModuloAccount({
  aperto,
  account,
  onChiudi,
  exchangeDisponibili,
  onSalvato,
}: {
  aperto: boolean;
  /** `null` = creazione. */
  account: Account | null;
  onChiudi: () => void;
  exchangeDisponibili: { id: number; name: string; slug: string; isCCXTSupported: boolean }[];
  onSalvato: (nome: string) => void;
}) {
  const inModifica = account != null;
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [exchangeId, setExchangeId] = useState('');
  const [chiave, setChiave] = useState('');
  const [segreto, setSegreto] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /** Svuota i campi segreti. Si chiama sempre, sia dopo l'invio sia alla
   *  chiusura: un segreto che resta nello stato di React è un segreto che
   *  resta in memoria e ricompare riaprendo il modulo. */
  function svuota() {
    setChiave('');
    setSegreto('');
    setPassphrase('');
  }

  /* All'apertura si caricano i valori correnti — tranne le chiavi, che non
     esistono da nessuna parte nel browser e restano vuote per definizione. */
  useEffect(() => {
    if (!aperto) return;
    setNome(account?.name ?? '');
    setDescrizione(account?.description ?? '');
    setExchangeId(account ? String(account.exchangeId) : '');
    svuota();
    setErrore(null);
  }, [aperto, account]);

  const nomePulito = nome.trim();
  const scelto = exchangeDisponibili.find((e) => String(e.id) === exchangeId);

  /* Chiave e segreto vanno insieme: metà coppia nuova e metà vecchia non
     autentica su nessun exchange. */
  const chiaviAMeta = inModifica && (chiave === '') !== (segreto === '');
  const sostituisceChiavi = inModifica && chiave !== '' && segreto !== '';

  const valido = inModifica
    ? nomePulito !== '' && !chiaviAMeta
    : nomePulito !== '' && exchangeId !== '' && chiave !== '' && segreto !== '';

  async function invia() {
    if (!valido || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      if (inModifica) {
        await accounts.aggiorna(account.id, {
          name: nomePulito,
          /* Stringa vuota e `null` non sono la stessa cosa: la colonna è
             nullable, e svuotare il campo deve azzerarla, non salvare "". */
          description: descrizione.trim() === '' ? null : descrizione.trim(),
          /* Le chiavi entrano nel corpo SOLO se riscritte: vedi il commento in
             testa a questo componente. */
          ...(sostituisceChiavi
            ? { apiKey: chiave, apiSecret: segreto, apiPassphrase: passphrase || null }
            : {}),
        } as Partial<Account>);
      } else {
        await accounts.crea({
          name: nomePulito,
          exchangeId: Number(exchangeId),
          ...(descrizione.trim() ? { description: descrizione.trim() } : {}),
          apiKey: chiave,
          apiSecret: segreto,
          ...(passphrase ? { apiPassphrase: passphrase } : {}),
        });
      }
      svuota();
      onSalvato(nomePulito);
    } catch (e) {
      /* Il messaggio d'errore del server non contiene la chiave, e non la si
         aggiunge: niente `console.error` con il corpo della richiesta dentro. */
      setErrore(e instanceof ErroreApi ? e.message : 'Salvataggio non riuscito.');
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={aperto}
      onChiudi={() => {
        svuota();
        setErrore(null);
        onChiudi();
      }}
      titolo={inModifica ? "Modifica l'account" : 'Aggiungi un account'}
      sottotitolo={
        inModifica
          ? 'le chiavi si sostituiscono, non si leggono'
          : 'le chiavi partono una sola volta, in questa richiesta'
      }
      pieda={
        <Pulsante variante="primario" largo onClick={invia} disabled={!valido || inCorso}>
          {inCorso
            ? 'Salvataggio in corso…'
            : inModifica
              ? 'Salva le modifiche'
              : 'Crea l’account'}
        </Pulsante>
      }
    >
      <div className="sb-colonna">
        <Avvertenza livello="cautela">
          {inModifica
            ? "Le chiavi salvate non sono leggibili: il backend non le restituisce. Qui si possono solo sostituire — lasciando i due campi vuoti restano quelle di adesso."
            : "Chiave e segreto attraversano il browser solo adesso, nel corpo di questa richiesta. Non vengono registrati, non finiscono in memoria del browser e non tornano più indietro: se li perdi, si rigenerano sull'exchange."}
        </Avvertenza>

        <Campo
          etichetta="Nome"
          testuale
          suggerimento="come lo riconosci nella lista"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          autoComplete="off"
        />

        <Campo
          etichetta="Descrizione"
          testuale
          suggerimento="facoltativa"
          value={descrizione}
          onChange={(e) => setDescrizione(e.target.value)}
          autoComplete="off"
        />

        {inModifica ? (
          /* Sola lettura con il motivo scritto accanto: un campo spento senza
             spiegazione si legge come un errore dell'interfaccia. */
          <div className="sb-campo">
            <div className="sb-campo__etichetta">
              <span className="sb-campo__nome">Exchange</span>
              <span className="sb-campo__suggerimento">non modificabile</span>
            </div>
            <div className="sb-m-voce" style={{ borderTop: 'none', paddingLeft: 0, paddingRight: 0 }}>
              <span className="sb-m-voce__chiave">
                {account.exchange?.name ?? `#${account.exchangeId}`}
                {account.exchange?.slug ? ` · ${account.exchange.slug}` : ''}
              </span>
            </div>
            {/* `sb-nota` e non `sb-campo__errore`: quest'ultimo è rosso, e il
                rosso qui dentro è riservato alle perdite e agli errori veri. */}
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
              Le chiavi API valgono solo per l'exchange che le ha emesse: per spostarsi su un altro
              exchange si crea un account nuovo, senza toccare questo.
            </div>
          </div>
        ) : (
          <Selettore
            etichetta="Exchange"
            suggerimento={`${exchangeDisponibili.length} disponibili`}
            valore={exchangeId}
            voci={[
              { valore: '', etichetta: 'scegli…' },
              ...exchangeDisponibili.map((e) => ({
                valore: String(e.id),
                etichetta: e.name,
                motivoSpento: e.isCCXTSupported === false ? 'non più supportato da ccxt' : undefined,
              })),
            ]}
            onCambia={setExchangeId}
          />
        )}

        {/* type=password su tutti e tre: la chiave non è meno sensibile del
            segreto, e su un telefono in treno lo schermo è pubblico. */}
        <Campo
          etichetta="Chiave API"
          type="password"
          testuale
          suggerimento={inModifica ? 'vuoto = non la cambio' : undefined}
          value={chiave}
          onChange={(e) => setChiave(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <Campo
          etichetta="Segreto API"
          type="password"
          testuale
          suggerimento={inModifica ? 'vuoto = non lo cambio' : undefined}
          value={segreto}
          onChange={(e) => setSegreto(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          errore={chiaviAMeta ? 'Chiave e segreto si sostituiscono insieme: compilali entrambi o lasciali entrambi vuoti.' : null}
        />
        <Campo
          etichetta="Passphrase"
          type="password"
          testuale
          suggerimento={
            inModifica ? 'solo alcuni exchange · segue le chiavi' : 'solo alcuni exchange'
          }
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />

        {sostituisceChiavi && (
          <Avvertenza livello="grave">
            Le chiavi vecchie vengono sovrascritte e non sono recuperabili da qui. La passphrase
            parte insieme a loro: se questo exchange ne vuole una e il campo è vuoto, viene
            azzerata. Se le nuove chiavi sono sbagliate i bot su questo account cominciano a
            fallire, quindi revoca le vecchie sull'exchange solo dopo aver visto che i saldi tornano
            a essere letti.
          </Avvertenza>
        )}

        {!inModifica && scelto?.isCCXTSupported === false && (
          <Avvertenza livello="cautela">
            La versione di ccxt installata non riconosce lo slug "{scelto.slug}": l'account si crea,
            ma nessun bot potrà operarci.
          </Avvertenza>
        )}

        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}
      </div>
    </Modale>
  );
}
